import * as React from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Slide from "@mui/material/Slide";
import {
  Autocomplete,
  Checkbox,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import withAuth from "../../../../../../Hoc/withAuth";
import PropTypes from "prop-types";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const generateMonths = () => {
  const monthsArray = [];
  const today = dayjs();
  for (let i = 6; i > 0; i--) {
    const targetMonth = today.subtract(i, "month");
    monthsArray.push({
      label: targetMonth.format("MMMM YYYY"),
      value: targetMonth.startOf("month").format("YYYY-MM-DD"),
    });
  }
  for (let i = 0; i < 6; i++) {
    const targetMonth = today.add(i, "month");
    monthsArray.push({
      label: targetMonth.format("MMMM YYYY"),
      value: targetMonth.startOf("month").format("YYYY-MM-DD"),
    });
  }
  return monthsArray;
};

const dynamicMonths = generateMonths();

const DuplicatePrevious = ({ selectedCard, user, onSuccess }) => {
  const [open, setOpen] = React.useState(false);
  const [selectMonth, setSelectMonth] = React.useState("");
  const [selectType, setSelectType] = React.useState("");

  const [selectedItems, setSelectedItems] = React.useState([]);

  const token = user.token;
  const config = { headers: { Authorization: `Bearer ${token}` } };
  const queryClient = useQueryClient();

  React.useEffect(() => {
    if (!selectedCard) return;
    let sourceMonth = "";
    let sourceType = "";
    switch (selectedCard.type) {
      case "forecast 1":
        sourceMonth = selectedCard.date;
        sourceType = "current";
        break;
      case "forecast 2":
        sourceMonth = selectedCard.date;
        sourceType = "forecast 1";
        break;
      case "current":
      default:
        sourceMonth = dayjs(selectedCard.date)
          .subtract(1, "month")
          .startOf("month")
          .format("YYYY-MM-DD");
        sourceType = "current";
        break;
    }
    setSelectMonth(sourceMonth);
    setSelectType(sourceType);
  }, [selectedCard]);

  const { data: sourceData, isLoading: isSourceLoading } = useQuery({
    // ... (this query remains the same)
    queryKey: ["sourceCampaignData", selectType, selectMonth],
    queryFn: async () => {
      if (!selectType || !selectMonth) return [];
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/campaignPlanning2`,
        { params: { planningType: selectType, date: selectMonth }, ...config }
      );
      return response.data ?? [];
    },
    enabled: open && !!selectType && !!selectMonth,
  });

  React.useEffect(() => {
    setSelectedItems([]);
  }, [sourceData]);

  const duplicateMutation = useMutation({
    mutationFn: (processedData) => {
      return axios.post(
        `${process.env.REACT_APP_API_URL}/campaignPlanning2/bulk`,
        processedData,
        config
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["campaignPlanningData", selectedCard],
      });
      if (onSuccess) onSuccess();
      handleClose();
    },
  });

  const handleClickOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    duplicateMutation.reset();
  };

  const handleDuplicate = () => {
    const itemsToDuplicate = sourceData.filter((item) =>
      selectedItems.some((selected) => selected.id === item.id)
    );

    if (itemsToDuplicate.length === 0) {
      alert("Please select at least one line item to duplicate.");
      return;
    }

    const payload = itemsToDuplicate.map((campaign) => {
      const { id, createdAt, updatedAt, ...rest } = campaign;
      return {
        ...rest,
        date: selectedCard.date,
        planningType: selectedCard.type,
      };
    });
    duplicateMutation.mutate(payload);
  };

  const getDialogTitleAndButtonText = () => {
    switch (selectedCard.type) {
      case "forecast 1":
        return {
          title: `Create Forecast 1 from Current Plan`,
          button: "Create Forecast 1",
        };
      case "forecast 2":
        return {
          title: `Create Forecast 2 from Forecast 1 Plan`,
          button: "Create Forecast 2",
        };
      default:
        return {
          title: "Duplicate Planning from a Previous Month",
          button: "Duplicate Planning",
        };
    }
  };

  const { title, button: buttonText } = getDialogTitleAndButtonText();

  return (
    <React.Fragment>
      <Button onClick={handleClickOpen}>{buttonText}</Button>
      <Dialog
        open={open}
        TransitionComponent={Transition}
        keepMounted
        onClose={handleClose}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>{title}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Typography color="error" variant="body2" gutterBottom>
                <b>Warning:</b> This will overwrite any line items with the same
                name for{" "}
                <b>
                  {selectedCard.title} ({selectedCard.type})
                </b>
                .
              </Typography>
            </Grid>

            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Source Month</InputLabel>
                <Select
                  value={selectMonth}
                  label="Source Month"
                  onChange={(e) => setSelectMonth(e.target.value)}
                >
                  {dynamicMonths.map((month) => (
                    <MenuItem key={month.value} value={month.value}>
                      {month.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Source Planning Type</InputLabel>
                <Select
                  value={selectType}
                  label="Source Planning Type"
                  onChange={(e) => setSelectType(e.target.value)}
                >
                  <MenuItem value="current">Current</MenuItem>
                  <MenuItem value="forecast 1">Forecast 1</MenuItem>
                  <MenuItem value="forecast 2">Forecast 2</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {isSourceLoading && (
              <Grid item xs={12} textAlign="center">
                <CircularProgress />
                <Typography>Loading source data...</Typography>
              </Grid>
            )}

            {sourceData && sourceData.length > 0 && (
              <Grid item xs={12}>
                <Autocomplete
                  multiple
                  id="line-item-selector"
                  options={sourceData}
                  disableCloseOnSelect
                  value={selectedItems}
                  onChange={(event, newValue) => {
                    setSelectedItems(newValue);
                  }}
                  getOptionLabel={(option) => option.name || "Unnamed Campaign"}
                  renderOption={(props, option, { selected }) => {
                    console.log("Rendering option:", option);
                    return (
                      <li {...props}>
                        <Checkbox
                          icon={icon}
                          checkedIcon={checkedIcon}
                          style={{ marginRight: 8 }}
                          checked={selected}
                        />
                        {option.name} {option.product && `(${option.product})`}
                        {}
                      </li>
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Line Items to Duplicate"
                      placeholder="Campaigns"
                    />
                  )}
                />
              </Grid>
            )}

            {duplicateMutation.isError && (
              <Grid item xs={12}>
                {/* ... (error alert remains the same) ... */}
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={duplicateMutation.isLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleDuplicate}
            variant="contained"
            color="primary"
            disabled={
              isSourceLoading ||
              selectedItems.length === 0 ||
              duplicateMutation.isLoading
            }
          >
            {duplicateMutation.isLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              `Duplicate ${selectedItems.length} Item(s)`
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
};

DuplicatePrevious.propTypes = {
  selectedCard: PropTypes.object.isRequired,
  user: PropTypes.object.isRequired,
  onSuccess: PropTypes.func,
};

export default withAuth(DuplicatePrevious);
